import type { OfferingModel } from 'interfaces/database';

interface ApplyVectorSVGPrintEffectOptions {
	addPrintEffect: boolean;
	applyPrintEffect: (
		svgElement: SVGElement,
		printEffect: OfferingModel['printEffect'],
	) => void;
	printEffect?: OfferingModel['printEffect'];
}

/**
 * Applies a DOM mutation to SVG markup and serializes the result with XML rules.
 *
 * Use this helper when SVG markup must be parsed before it can be changed. The
 * XML serialization step keeps text content such as non-breaking spaces valid
 * for `image/svg+xml` consumers instead of producing HTML-only entities.
 *
 * @param svgContent Source SVG markup to parse and mutate
 * @param mutateSVGElement Callback that receives the parsed root SVG element
 * @returns Original SVG markup when no root element is found; otherwise XML-safe mutated SVG markup
 */
function applyVectorSVGDOMMutation(
	svgContent: string,
	mutateSVGElement: (svgElement: SVGElement) => void,
): string {
	const temporaryElement = document.createElement('div');
	temporaryElement.innerHTML = svgContent;

	try {
		const svgElement = temporaryElement.firstElementChild as SVGElement | null;

		if (!svgElement) {
			return svgContent;
		}

		mutateSVGElement(svgElement);

		return new XMLSerializer().serializeToString(svgElement);
	} finally {
		temporaryElement.remove();
	}
}

/**
 * Applies the configured print effect to vector SVG markup when both the page
 * render flow requests it and the offering provides a print effect.
 *
 * The original SVG is returned unchanged when `addPrintEffect` is false or
 * `printEffect` is not configured, avoiding an unnecessary DOM serialization
 * pass. When a print effect is applied, the mutated SVG is serialized as XML so
 * text content remains valid for Blob-backed SVG image loading.
 *
 * @param svgContent Source SVG markup from the vector object
 * @param options {@link ApplyVectorSVGPrintEffectOptions} controls and dependencies for the mutation
 * @param options.addPrintEffect Whether the current render should apply print effects
 * @param options.applyPrintEffect Callback that mutates the parsed SVG element for the configured effect
 * @param options.printEffect Offering print effect to apply when available
 * @returns Original SVG markup when no effect is applied; otherwise XML-safe SVG markup with the print effect applied
 */
export function applyVectorSVGPrintEffect(
	svgContent: string,
	options: ApplyVectorSVGPrintEffectOptions,
): string {
	if (
		!options.addPrintEffect
		|| !options.printEffect
	) {
		return svgContent;
	}

	return applyVectorSVGDOMMutation(
		svgContent,
		(svgElement) => options.applyPrintEffect(
			svgElement,
			options.printEffect,
		),
	);
}
